import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, forkJoin, mergeMap, Observable, of } from 'rxjs';
import { MetricsData } from '@app/driver-metrics/model/driver-metrics.interface';
import { AdmService } from '@services/adm.service';
import { Building } from '@app/shared/models/building.interface';
import { AdmFilterCriteria } from '@app/driver-metrics/timeseries/adm-filter.service';
import { SensorNodeService } from '@services/sensor-node.service';

export abstract class AbstractAdmDataSource<T> implements DataSource<T> {
  protected loadingSubject = new BehaviorSubject(false);
  protected countSubject = new BehaviorSubject(0);

  get isLoading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  get hasData(): boolean {
    return this.countSubject.value > 0;
  }

  get counter$(): Observable<number> {
    return this.countSubject.asObservable();
  }

  abstract loadDriverMetrics(filterCriteria: AdmFilterCriteria, paginationParams: PaginationParams): void;

  abstract connect(): Observable<T[]>;

  abstract disconnect(): void;
}

export class DriverMetricsDataSource extends AbstractAdmDataSource<any> {
  private readonly dataSubject = new BehaviorSubject<any[]>([]);

  constructor(
    private readonly admService: AdmService,
    private readonly nodeService: SensorNodeService,
    private readonly building: Building
  ) {
    super();
  }

  connect(): Observable<any[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(): void {
    this.dataSubject.complete();
  }

  loadDriverMetrics(filterCriteria: AdmFilterCriteria, pagParams: PaginationParams): void {
    this.loadingSubject.next(true);
    this.admService
      .getAdmData(this.building, filterCriteria, pagParams)
      .pipe(
        mergeMap((data) => {
          const nodes$ = this.nodeService.getNodesByIds(
            ([...new Set(data.content.map((d) => d.nodeId))] as number[]).sort()
          );
          return forkJoin([nodes$, of(data)]);
        })
      )
      .subscribe({
        next: (data) => {
          const nodes = data[0];
          const paginatedMetricsData = data[1];
          paginatedMetricsData.content.forEach((metric: MetricsData) => {
            const node = nodes.find((n) => n.id === metric.nodeId);
            metric.sensorNodeLink = node
              ? `/buildings/${this.building.id}/${node.floorId}/sensor-nodes?n=${node.id}`
              : '';
          });
          this.dataSubject.next(paginatedMetricsData.content);
          this.loadingSubject.next(false);
          this.countSubject.next(paginatedMetricsData.totalElements);
        },
        error: (error) => {
          console.error('Error loading driver metrics:', error);
        }
      });
  }
}

export class MockAdmDataSource extends AbstractAdmDataSource<any> {
  private readonly dataSubject = new BehaviorSubject<any[]>([]);

  constructor(private readonly admService: AdmService, private readonly building: Building) {
    super();
  }

  connect(): Observable<any[]> {
    return this.dataSubject.asObservable();
  }

  disconnect(): void {
    this.dataSubject.complete();
  }

  loadDriverMetrics(filterCriteria: AdmFilterCriteria, pagParams: PaginationParams): void {
    this.loadingSubject.next(true);
    // Mock data loading logic
    const mockData = [
      { id: 1, name: 'Metric 1' },
      { id: 2, name: 'Metric 2' }
    ];
    this.dataSubject.next(mockData);
    this.loadingSubject.next(false);
    this.countSubject.next(mockData.length);
  }
}

export interface PaginationParams {
  pageSize: number;
  pageNumber: number;
}
